const SignaturePad = require('signature_pad').default
const axios = require('axios')

const is = require('../plugins/helper/is')
const Swal = require('../plugins/sweet/Alert').default

const DIGITAL_TAB_ID = 'pills-sign-document-digital'
const DIGITAL_TRIGGER_TAB_ID = 'id-sign-document-digital'
const SELECTOR_CANVAS = '.signature-pad-canvas'
const SELECTOR_CLEAR_BTN = '[id^="button_clear_signature"]'
const SELECTOR_SAVE_BTN = '[id^="button_save_signature"]'
const SELECTOR_ORIENTATION_ALERT = '#orientation-alert'
const IS_IOS = /iPhone|iPad|iPod/i.test(navigator.userAgent || navigator.vendor || window.opera)

/**
 * Devuelve la orientacion actual de la pantalla.
 * @returns {string|null}
 */
const getCurrentOrientation = () => {
  if (screen.orientation && typeof screen.orientation.type === 'string') {
    return screen.orientation.type
  }
  return (screen.mozOrientation || screen.msOrientation || null)
}

/**
 * Determina si la pantalla esta en portrait.
 * @returns {boolean}
 */
const isPortrait = () => {
  const orientation = getCurrentOrientation()
  if (orientation && /portrait/.test(orientation)) {
    return true
  }
  if (orientation && /landscape/.test(orientation)) {
    return false
  }
  return window.innerHeight >= window.innerWidth
}

/**
 * Intenta forzar la orientacion horizontal del dispositivo.
 * @returns {Promise<boolean>}
 */
const tryLockLandscape = async () => {
  if (IS_IOS) {
    // iOS Safari no soporta screen.orientation.lock; se guia al usuario.
    return false
  }
  if (!screen.orientation || typeof screen.orientation.lock !== 'function') {
    return false
  }
  try {
    await screen.orientation.lock('landscape')
    return true
  } catch (err) {
    if (is && typeof is.captureException === 'function') {
      is.captureException(err)
    }
    return false
  }
}

/**
 * Muestra u oculta el aviso de orientacion recomendado.
 * @param {boolean} visible
 */
const toggleOrientationAlert = (visible) => {
  const alertEl = document.querySelector(SELECTOR_ORIENTATION_ALERT)
  if (!alertEl) {
    return
  }
  alertEl.style.display = visible ? 'block' : 'none'
}

/**
 * Inicializa una instancia de SignaturePad sobre un canvas dado.
 * @param {HTMLCanvasElement} signatureCanvas
 * @param {HTMLDivElement|null} signatureContainer
 * @returns {SignaturePad|null}
 */
const initSignaturePadInstance = (signatureCanvas, signatureContainer) => {
  if (!signatureCanvas || !is.node(signatureCanvas)) {
    return null
  }

  const getWidth = () => {
    if (is.node(signatureContainer)) {
      // Si el contenedor esta oculto (display:none de un tab inactivo),
      // clientWidth vale 0. En ese caso, usamos el ancho de la ventana
      // para que el canvas tenga un tamano utilizable al mostrarse.
      if (signatureContainer.clientWidth > 0) {
        return signatureContainer.clientWidth
      }
      return Math.min(window.innerWidth - 32, 720)
    }
    return screen.width
  }
  const getHeight = () => {
    if (is.node(signatureContainer)) {
      if (signatureContainer.clientHeight > 0) {
        return signatureContainer.clientHeight
      }
      return 220
    }
    return 220
  }

  const resizeCanvas = () => {
    const newWidth = getWidth()
    const newHeight = getHeight()
    const newRatio = Math.max(window.devicePixelRatio || 1, 1)
    signatureCanvas.width = newWidth * newRatio
    signatureCanvas.height = newHeight * newRatio
    signatureCanvas.style.width = `${newWidth}px`
    signatureCanvas.style.height = `${newHeight}px`
    const ctx = signatureCanvas.getContext('2d')
    if (ctx) {
      ctx.scale(newRatio, newRatio)
    }
  }

  resizeCanvas()

  const signaturePad = new SignaturePad(signatureCanvas, {
    minWidth: 0.8,
    maxWidth: 2.4,
    penColor: '#0d6efd',
  })

  if (screen.orientation && typeof screen.orientation.addEventListener === 'function') {
    screen.orientation.addEventListener('change', () => {
      resizeCanvas()
      if (!signaturePad.isEmpty()) {
        signaturePad.clear()
      }
    })
  } else if (typeof window !== 'undefined' && typeof window.orientation !== 'undefined') {
    window.addEventListener('orientationchange', () => {
      resizeCanvas()
      if (!signaturePad.isEmpty()) {
        signaturePad.clear()
      }
    })
  }

  // Fallback adicional: tambien responder al resize de la ventana.
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', () => {
      resizeCanvas()
    })
  }

  return {
    signaturePad,
    resizeCanvas,
  }
}

/**
 * Maneja el envio de la firma al backend.
 * @param {Event} event
 * @param {SignaturePad} signaturePad
 * @param {HTMLDivElement|null} signatureContainer
 * @param {HTMLElement} saveButton
 */
const handleSaveSignature = async (event, signaturePad, signatureContainer, saveButton) => {
  const trigger = event.delegateTarget || event.currentTarget || saveButton
  if (!signaturePad || signaturePad.isEmpty()) {
    new Swal({
      text: 'Debe dibujar su firma en el panel indicado.',
      allowOutsideClick: false,
      showConfirmButton: false,
      showCancelButton: false,
      position: 'top-start',
      icon: 'warning',
      timer: 2000,
    })
    return false
  }

  const width = (signatureContainer && signatureContainer.clientWidth) || screen.width
  const height = (signatureContainer && signatureContainer.clientHeight) || 220
  const file = await is.asyncTakeCareOfCompressingOnly(is.dataURLtoFile(signaturePad.toDataURL('image/png'), 'firma-digital'), width, height)
  if (!is.file(file)) {
    return false
  }

  const action = is.getAttributeValue(trigger, 'action')
  const origen = is.getAttributeValue(trigger, 'origen')
  const redirect = is.getAttributeValue(trigger, 'redirect')
  // El campo se llama 'document' (no 'signature') para ser compatible con
  // los endpoints de firma del backend (sendSignatureBankApplication y
  // sendPartialSignatureBankApplication esperan $request->file('document')).
  // Tambien se envia 'signature' para retrocompatibilidad con cualquier
  // endpoint previo que pueda leerlo.
  const form = is.objectToFormData({ origen, document: file, signature: file })

  // Firma parcial multi-banco: si la vista renderizo checkboxes con name="bancos[]",
  // adjuntamos SOLO los que el cliente dejo marcados y que no estan deshabilitados
  // (los bancos ya firmados llegan con `disabled` para no ser re-firmados).
  if (typeof document !== 'undefined' && document.querySelectorAll) {
    const bancosChecked = document.querySelectorAll('input[name="bancos[]"]:checked:not(:disabled)')
    if (bancosChecked && bancosChecked.length > 0) {
      bancosChecked.forEach((cb) => {
        if (cb && typeof cb.value === 'string' && cb.value.length > 0) {
          form.append('bancos[]', cb.value)
        }
      })
    }
  }

  // Mostrar spinner del boton
  const label = trigger.querySelector('.button-save-label')
  const spinner = trigger.querySelector('.spinner-border')
  if (label) {
    label.classList.add('d-none')
  }
  if (spinner) {
    spinner.classList.remove('d-none')
  }
  trigger.disabled = true

  axios.post(action, form).then((response) => {
    new Swal({
      text: (response && response.data && response.data.success) ? response.data.success : 'Datos enviados correctamente.',
      allowOutsideClick: false,
      showConfirmButton: false,
      showCancelButton: false,
      position: 'top-start',
      icon: 'success',
      timer: 1500,
    }).finalize(() => {
      if (response && response.data && response.data.redirect) {
        window.location.href = response.data.redirect
        return
      }
      if (redirect) {
        window.location.href = redirect
        return
      }
      window.location.reload()
    })
  }).catch((response) => {
    // Restaurar boton
    if (label) {
      label.classList.remove('d-none')
    }
    if (spinner) {
      spinner.classList.add('d-none')
    }
    trigger.disabled = false

    const errorMessage = (response && response.response && response.response.data && response.response.data.message)
      ? response.response.data.message
      : 'Ocurrio un error al enviar la firma. Por favor intente nuevamente.'

    if (typeof console !== 'undefined' && console.error) {
      console.error(response)
    }
    if (is && typeof is.captureException === 'function') {
      is.captureException(response)
    }

    new Swal({
      text: errorMessage,
      allowOutsideClick: false,
      showConfirmButton: false,
      showCancelButton: false,
      position: 'top-start',
      icon: 'error',
      timer: 3000,
    })
  })

  return false
}

/**
 * Asocia los listeners al boton limpiar.
 * @param {HTMLButtonElement} clearButton
 * @param {SignaturePad} signaturePad
 */
const bindClearButton = (clearButton, signaturePad) => {
  if (!clearButton || !signaturePad) {
    return
  }
  clearButton.addEventListener('click', (e) => {
    try {
      if (!signaturePad.isEmpty()) {
        signaturePad.clear()
      }
    } catch (err) {
      if (is && typeof is.captureException === 'function') {
        is.captureException(err)
      }
    }
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault()
    }
    return false
  })
}

/**
 * Asocia los listeners al boton guardar.
 * @param {HTMLButtonElement} saveButton
 * @param {SignaturePad} signaturePad
 * @param {HTMLDivElement|null} signatureContainer
 */
const bindSaveButton = (saveButton, signaturePad, signatureContainer) => {
  if (!saveButton || !signaturePad) {
    return
  }
  saveButton.addEventListener('click', (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault()
    }
    return handleSaveSignature(e, signaturePad, signatureContainer, saveButton)
  })
}

// Lista de canvases inicializados con su callback de resize.
// Se reasignan desde initAllSignaturePads() para que los handlers
// puedan redimensionarlos al hacerse visible su tab.
const signaturePadInstances = []

/**
 * Maneja la activacion de la pestana DIGITAL: muestra alerta, bloquea
 * orientacion horizontal cuando es posible y redimensiona los canvases
 * que se estaban inicializando cuando el contenedor estaba oculto.
 */
const handleDigitalTabActivation = () => {
  // Mostrar/ocultar alerta segun orientacion
  if (isPortrait()) {
    toggleOrientationAlert(true)
  } else {
    toggleOrientationAlert(false)
  }

  // Reajustar el tamano de los canvases ahora que el contenedor es visible.
  // Esto resuelve el caso en que se inicializaron con clientWidth = 0
  // porque el tab estaba oculto (display:none por Bootstrap).
  signaturePadInstances.forEach((entry) => {
    if (entry && typeof entry.resizeCanvas === 'function') {
      // Un pequeno delay asegura que el navegador ya aplico el cambio
      // de visibilidad antes de recalcular las dimensiones.
      setTimeout(() => {
        try {
          entry.resizeCanvas()
        } catch (err) {
          if (is && typeof is.captureException === 'function') {
            is.captureException(err)
          }
        }
      }, 50)
    }
  })

  // Intentar forzar landscape solo en movil
  if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 900px)').matches) {
    tryLockLandscape()
  }
}

/**
 * Inicializa todos los canvases de firma digital que encuentre en la pagina.
 */
const initAllSignaturePads = () => {
  const canvases = document.querySelectorAll(SELECTOR_CANVAS)
  if (!canvases || canvases.length === 0) {
    return
  }

  canvases.forEach((canvas) => {
    const container = canvas.closest('.signature-pad-wrapper')

    // Determinar el sufijo del ID para encontrar clear/save buttons correspondientes
    const canvasId = canvas.id || ''
    const suffix = canvasId.replace('signature_canvas', '').replace(/^_/, '')
    const clearSelector = suffix ? `#button_clear_signature_${suffix}` : '#button_clear_signature'
    const saveSelector = suffix ? `#button_save_signature_${suffix}` : '#button_save_signature'

    const clearButton = document.querySelector(clearSelector)
    const saveButton = document.querySelector(saveSelector)
    if (!clearButton && !saveButton) {
      return
    }

    const instance = initSignaturePadInstance(canvas, container)
    if (!instance || !instance.signaturePad) {
      return
    }

    signaturePadInstances.push(instance)
    bindClearButton(clearButton, instance.signaturePad)
    bindSaveButton(saveButton, instance.signaturePad, container)
  })

  // Listener para la pestana DIGITAL
  const digitalTabTrigger = document.getElementById(DIGITAL_TRIGGER_TAB_ID)
  if (digitalTabTrigger) {
    digitalTabTrigger.addEventListener('shown.bs.tab', handleDigitalTabActivation)
    // Tambien al cargar la pagina si la pestana digital esta activa
    if (digitalTabTrigger.classList.contains('active')) {
      handleDigitalTabActivation()
    }
  }
}

try {
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAllSignaturePads)
    } else {
      initAllSignaturePads()
    }
  }
} catch (err) {
  if (is && typeof is.captureException === 'function') {
    is.captureException(err)
  }
}
